import React, { useState, useEffect } from 'react';
import { FaFlag, FaEye, FaCheck, FaTimes, FaSearch, FaFilter, FaUser, FaCalendarAlt } from 'react-icons/fa';
import { adminAPI } from '../../services/api';
import { getUploadUrl } from '../../utils/helpers';
import { toast } from 'react-toastify';

const AdminReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    page: 1,
    limit: 20
  });

  useEffect(() => {
    fetchReports();
  }, [filters]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching reports with filters:', filters);

      const response = await adminAPI.getReports(filters);
      console.log('📋 Reports API response:', response);

      if (response && response.success) {
        setReports(response.reports || []);
        setPagination(response.pagination || {
          page: 1,
          limit: 20,
          total: 0,
          pages: 0
        });
        setError(null);
      } else {
        const errorMessage = response?.message || 'Unknown error occurred';
        console.error('❌ Reports API error:', errorMessage);
        setError(errorMessage);
      }
    } catch (error) {
      console.error('❌ Error fetching reports:', error);

      // Safe error object access
      const errorObj = error || {};
      console.error('Error details:', {
        message: errorObj.message,
        status: errorObj.response?.status,
        statusText: errorObj.response?.statusText,
        data: errorObj.response?.data,
        code: errorObj.code,
        name: errorObj.name
      });

      let errorMessage = 'Failed to load reports';

      // Check for network errors
      if (errorObj.code === 'NETWORK_ERROR' ||
          errorObj.code === 'ERR_NETWORK' ||
          errorObj.message?.includes('Network Error') ||
          errorObj.message?.includes('Failed to fetch') ||
          !errorObj.response) {
        errorMessage = '🔌 Backend server is not running. Please start the backend server on port 5117';

        // Development fallback - mock data for testing
        if (import.meta.env.DEV) {
          console.log('🔧 Development mode: Using mock reports data');
          setReports([
            {
              id: 1,
              reporter_name: 'John Doe',
              reporter_email: 'john@example.com',
              reported_user_name: 'Jane Smith',
              reported_user_email: 'jane@example.com',
              reason: 'spam',
              description: 'This user is sending spam messages',
              status: 'pending',
              time: Math.floor(Date.now() / 1000),
              reported_user_verified: false,
              reported_user_vip: false
            }
          ]);
          setPagination({
            page: 1,
            limit: 20,
            total: 1,
            totalPages: 1,
            hasMore: false
          });
          setError(null);
          setLoading(false);
          return;
        }
      } else if (errorObj.response?.status === 401) {
        errorMessage = '🔐 Unauthorized - please login again as admin';
      } else if (errorObj.response?.status === 403) {
        errorMessage = '🚫 Access denied - admin privileges required';
      } else if (errorObj.response?.status === 404) {
        errorMessage = '📭 No reports found';
      } else if (errorObj.response?.status === 500) {
        errorMessage = '🔧 Server error - please try again later';
      } else if (errorObj.response?.data?.message) {
        errorMessage = errorObj.response.data.message;
      } else if (errorObj.message) {
        errorMessage = `Error: ${errorObj.message}`;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : 1
    }));
  };

  const handleViewReport = async (reportId) => {
    try {
      console.log('🔍 Fetching report details for ID:', reportId);
      const response = await adminAPI.getReportDetails(reportId);
      console.log('📋 Report details response:', response);

      if (response && response.success) {
        setSelectedReport(response);
        setShowReportModal(true);
      } else {
        const errorMessage = response?.message || 'Failed to load report details';
        console.error('❌ Report details API error:', errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Error fetching report details:', error);
      // Safe error object access
      const errorObj = error || {};
      console.error('Error details:', {
        message: errorObj.message,
        status: errorObj.response?.status,
        statusText: errorObj.response?.statusText,
        data: errorObj.response?.data,
        code: errorObj.code,
        name: errorObj.name
      });

      let errorMessage = 'Failed to load report details';

      // Check for network errors
      if (errorObj.code === 'NETWORK_ERROR' ||
          errorObj.code === 'ERR_NETWORK' ||
          errorObj.message?.includes('Network Error') ||
          errorObj.message?.includes('Failed to fetch') ||
          !errorObj.response) {
        errorMessage = '🔌 Backend server is not running. Please start the backend server on port 5117';
      } else if (errorObj.response?.status === 401) {
        errorMessage = '🔐 Unauthorized - please login again as admin';
      } else if (errorObj.response?.status === 403) {
        errorMessage = '🚫 Access denied - admin privileges required';
      } else if (errorObj.response?.status === 404) {
        errorMessage = '📭 Report not found';
      } else if (errorObj.response?.status === 500) {
        errorMessage = '🔧 Server error - please try again later';
      } else if (errorObj.response?.data?.message) {
        errorMessage = errorObj.response.data.message;
      } else if (errorObj.message) {
        errorMessage = `Error: ${errorObj.message}`;
      }

      toast.error(errorMessage);
    }
  };

  const handleUpdateReportStatus = async (reportId, status, adminNotes = '') => {
    try {
      console.log('🔄 Updating report status:', { reportId, status, adminNotes });
      const response = await adminAPI.updateReportStatus(reportId, {
        status,
        admin_notes: adminNotes
      });
      console.log('📋 Update report status response:', response);

      if (response && response.success) {
        toast.success('Report status updated successfully');
        setShowReportModal(false);
        fetchReports();
      } else {
        const errorMessage = response?.message || 'Failed to update report status';
        console.error('❌ Update report status API error:', errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Error updating report status:', error);
      // Safe error object access
      const errorObj = error || {};
      console.error('Error details:', {
        message: errorObj.message,
        status: errorObj.response?.status,
        statusText: errorObj.response?.statusText,
        data: errorObj.response?.data,
        code: errorObj.code,
        name: errorObj.name
      });

      let errorMessage = 'Failed to update report status';

      // Check for network errors
      if (errorObj.code === 'NETWORK_ERROR' ||
          errorObj.code === 'ERR_NETWORK' ||
          errorObj.message?.includes('Network Error') ||
          errorObj.message?.includes('Failed to fetch') ||
          !errorObj.response) {
        errorMessage = '🔌 Backend server is not running. Please start the backend server on port 5117';
      } else if (errorObj.response?.status === 401) {
        errorMessage = '🔐 Unauthorized - please login again as admin';
      } else if (errorObj.response?.status === 403) {
        errorMessage = '🚫 Access denied - admin privileges required';
      } else if (errorObj.response?.status === 404) {
        errorMessage = '📭 Report not found';
      } else if (errorObj.response?.status === 500) {
        errorMessage = '🔧 Server error - please try again later';
      } else if (errorObj.response?.data?.message) {
        errorMessage = errorObj.response.data.message;
      } else if (errorObj.message) {
        errorMessage = `Error: ${errorObj.message}`;
      }

      toast.error(errorMessage);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Pending</span>;
      case 'reviewed':
        return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Reviewed</span>;
      case 'resolved':
        return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Resolved</span>;
      default:
        return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';

    // MSSQL timestamp is in seconds, convert to milliseconds for JavaScript Date
    const date = new Date(timestamp * 1000);

    // Check if date is valid
    if (isNaN(date.getTime())) return 'Invalid Date';

    // Return YYYY-MM-DD format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const ReportModal = ({ report, onClose }) => {
    const [adminNotes, setAdminNotes] = useState(report.report.admin_notes || '');
    const [actionLoading, setActionLoading] = useState(false);

    const handleStatusChange = async (status) => {
      setActionLoading(true);
      await handleUpdateReportStatus(report.report.id, status, adminNotes);
      setActionLoading(false);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Report Details</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Reporter Info */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
                  <FaUser className="w-5 h-5 mr-2" />
                  Reporter
                </h3>
                <div className="space-y-2">
                  <div><strong>Name:</strong> {report.report.reporter_name}</div>
                  <div><strong>Email:</strong> {report.report.reporter_email}</div>
                  <div><strong>Report Date:</strong> {formatDate(report.report.time)}</div>
                </div>
              </div>

              {/* Reported User Info */}
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-red-900 mb-3 flex items-center">
                  <FaFlag className="w-5 h-5 mr-2" />
                  Reported User
                </h3>
                <div className="space-y-2">
                  <div><strong>Name:</strong> {report.report.reported_user_name}</div>
                  <div><strong>Email:</strong> {report.report.reported_user_email}</div>
                  <div><strong>Verified:</strong> {report.report.reported_user_verified ? 'Yes' : 'No'}</div>
                  <div><strong>VIP:</strong> {report.report.reported_user_vip ? 'Yes' : 'No'}</div>
                </div>
              </div>
            </div>

            {/* Report Details */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-3">Report Information</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <strong>Reason:</strong>
                    <div className="mt-1 px-2 py-1 bg-white rounded border text-sm">{report.report.reason}</div>
                  </div>
                  <div>
                    <strong>Status:</strong>
                    <div className="mt-1">{getStatusBadge(report.report.status)}</div>
                  </div>
                </div>
                {report.report.description && (
                  <div>
                    <strong>Description:</strong>
                    <div className="mt-1 px-3 py-2 bg-white rounded border text-sm whitespace-pre-wrap">
                      {report.report.description}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Reported User Photos */}
            {report.reported_user_photos && report.reported_user_photos.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3">Reported User's Photos</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {report.reported_user_photos.map((photo) => (
                    <img
                      key={photo.id}
                      src={getUploadUrl(`user-${report.report.reported_user_id}/${photo.path}`)}
                      alt="Reported user photo"
                      className="w-full h-24 object-cover rounded-lg border"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Admin Notes */}
            {report.report.status !== 'pending' && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3">Admin Notes</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">
                    <strong>Reviewed by:</strong> {report.report.reviewed_by ? `Admin ID: ${report.report.reviewed_by}` : 'N/A'}<br />
                    <strong>Reviewed at:</strong> {formatDate(report.report.reviewed_at)}<br />
                    <strong>Notes:</strong> {report.report.admin_notes || 'No notes'}
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {report.report.status === 'pending' && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3">Admin Notes</h3>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes about this report..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4"
                  rows={3}
                />

                <div className="flex space-x-3">
                  <button
                    onClick={() => handleStatusChange('resolved')}
                    disabled={actionLoading}
                    className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {actionLoading ? 'Processing...' : 'Resolve Report'}
                  </button>
                  <button
                    onClick={() => handleStatusChange('reviewed')}
                    disabled={actionLoading}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {actionLoading ? 'Processing...' : 'Mark as Reviewed'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 lg:space-y-8 xl:space-y-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900">Reports Management</h1>
          <p className="text-gray-600 text-sm sm:text-base">Review and manage user reports</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100">
              <FaFlag className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Reports</p>
              <p className="text-2xl font-bold text-gray-900">
                {reports.filter(r => r.status === 'pending').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <FaEye className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Reviewed</p>
              <p className="text-2xl font-bold text-gray-900">
                {reports.filter(r => r.status === 'reviewed').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <FaCheck className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Resolved</p>
              <p className="text-2xl font-bold text-gray-900">
                {reports.filter(r => r.status === 'resolved').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="reviewed">Reviewed</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>

          <div className="flex items-end md:col-span-2">
            <button
              onClick={fetchReports}
              className="w-full md:w-auto bg-pink-600 text-white py-2 px-6 rounded-md hover:bg-pink-700"
            >
              <FaFilter className="inline w-4 h-4 mr-2" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
          </div>
        ) : error ? (
          <div className="p-6 text-center text-red-600">{error}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reporter</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reported User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{report.reporter_name}</div>
                        <div className="text-sm text-gray-500">{report.reporter_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{report.reported_user_name}</div>
                        <div className="text-sm text-gray-500">{report.reported_user_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{report.reason}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(report.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(report.time)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewReport(report.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <FaEye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleFilterChange('page', pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaChevronLeft className="w-4 h-4" />
                    </button>

                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>

                    <button
                      onClick={() => handleFilterChange('page', pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Report Modal */}
      {showReportModal && selectedReport && (
        <ReportModal
          report={selectedReport}
          onClose={() => setShowReportModal(false)}
        />
      )}
    </div>
  );
};

export default AdminReports;